import { useEffect, useState } from "react";

import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { FileUploadLite } from "@/Common/Components/FileUpload/FileUploadLite";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PdfPageDetails } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FaxDirectorySelectorComponent } from "@/ContactDirectory/Components/FaxDirectorySelectorComponent";
import { DirectoryFaxUpdateOptions } from "@/ContactDirectory/Models/DirectoryFaxUpdateOptions";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";
import { FaxAndSignatureClinicianSelection, FaxAndSignatureClinicianSelectionState } from "@/ExportableFile/Components/FaxAndSignatureClinicianSelection";
import { FaxCoverSheetEditorSection } from "@/ExportableFile/Components/FaxCoverSheetEditorSection";
import { IFaxCoverSheetProps } from "@/ExportableFile/Components/IFaxInformationProps";

import "./OutboundFaxCreationPopup.scss";

import img_icon_myle_pictogrammes_documents_31 from "@/Globals/_images/icons/documents/MYLE-Pictogrammes-Documents-31.png";

interface IOutboundFaxCreationPopupProps {
    popupTitle?: string;
    showX?: boolean;
    returnFaxSender?: boolean;
    initialFaxNumber?: string;
    selectedDocumentPages?: PdfPageDetails[];
    onCancel?: () => void;
    onSubmit?: (faxDetails: InboundFaxDetails, onCompleted?: VoidFunction) => void;
}

function OutboundFaxCreationPopup({
    onCancel,
    onSubmit,
    initialFaxNumber,
    popupTitle,
    selectedDocumentPages,
    showX = false,
    returnFaxSender = false
}: Readonly<IOutboundFaxCreationPopupProps>): JSX.Element {
    const { getText } = useTranslation();
    const [destinatorInfoState, setDestinatorInfoState] = useState<FaxAndSignatureClinicianSelectionState>(null);
    const [filePath, setFilePath] = useState<string>("");
    const [isFaxValid, setIsValidFax] = useState<boolean>(false);
    const [directoryFaxInfoState, setDirectoryFaxInfoState] = useState<DirectoryFaxUpdateOptions>(null);
    const [pdfCoverPageParams, setPdfCoverPageParams] = useState<IFaxCoverSheetProps>({});
    const [isSendButtonEnabled, setIsSendButtonEnabled] = useState<boolean>(false);
    const [showCoverSheetValidationErrors, setShowCoverSheetValidationErrors] = useState<boolean>(false);
    const [isCoverSheetSubjectValid, setIsCoverSheetSubjectValid] = useState<boolean>(true);
    const [isCoverSheetMessageValid, setIsCoverSheetMessageValid] = useState<boolean>(true);

    const isDirectoryNameValid = !IsNullOrWhiteSpace(destinatorInfoState?.CustomClinicianName || directoryFaxInfoState?.description);
    const isSelectedFaxNumberValid = (cacheStore.ClinicSettings.EnableContactDirectory ? isFaxValid : true);
    const isAttachmentValid = !returnFaxSender ? !IsNullOrWhiteSpace(filePath) : true;
    const isDirectoryFaxNumberValid = !IsNullOrWhiteSpace(destinatorInfoState?.CustomClinicianFaxNumber || directoryFaxInfoState?.faxNumber);

    const isFaxAllowed = isDirectoryFaxNumberValid && isDirectoryNameValid && isSelectedFaxNumberValid;
    const showSelectedDocumentPages = returnFaxSender && cacheStore.ClinicSettings.EnableFaxCoverSheet && selectedDocumentPages?.length > 0;

    useEffect(() => {
        const shouldEnableButton = !returnFaxSender ? (isFaxAllowed && isAttachmentValid) : isFaxAllowed;

        setIsSendButtonEnabled(shouldEnableButton);
    }, [isFaxAllowed, isAttachmentValid, returnFaxSender]);

    function onFaxClick(): void {
        setIsSendButtonEnabled(false);

        if (pdfCoverPageParams?.GenerateCoverPage && (!isCoverSheetMessageValid || !isCoverSheetSubjectValid)) {
            setShowCoverSheetValidationErrors(true);
            setIsSendButtonEnabled(true);
            return;
        }

        const phoneNumber = directoryFaxInfoState?.selectedEntity?.getRawPhoneNumber() || destinatorInfoState?.CustomClinicianPhoneNumber;
        const faxNumber = directoryFaxInfoState?.faxNumber || destinatorInfoState?.CustomClinicianFaxNumber;
        const displayName = directoryFaxInfoState?.description || destinatorInfoState?.CustomClinicianName;
        const fileName = filePath.replace(/\\/g, "/").split("/").pop();

        const faxDetails: InboundFaxDetails = {
            faxNumber,
            fileName,
            displayName,
            phoneNumber,
            pdfCoverPageParams
        };

        onSubmit(faxDetails, () => setIsSendButtonEnabled(true));
    }

    const footer: JSX.Element = (
        <div className="modal-footer">
            <button
                className="btn btn-default push-right-5"
                onClick={() => onCancel?.()}
            >
                {getText("CancelLabel")}
            </button>
            <button
                className="btn btn-primary push-right-5"
                onClick={() => onFaxClick()}
                disabled={!isSendButtonEnabled}
            >
                {getText("FaxLabel")}
            </button>
        </div>
    );

    return (
        <Modal
            centerContent
            title={popupTitle}
            showX={showX}
            width={500}
            showOverlay
            draggable
            onClose={() => onCancel?.()}
            footer={footer}
            overlayZIndex={99}
            maxHeight="550px"
        >
            <div className="outbound-fax-creation-component">
                <FeatureFlagControl
                    flag={cacheStore.ClinicSettings.EnableContactDirectory}
                    activeFeature={(
                        <div className="margin-lg-left margin-lg-bottom">
                            <FaxDirectorySelectorComponent
                                onFaxUpdate={option => setDirectoryFaxInfoState(option)}
                                isFaxValid={isValid => setIsValidFax(isValid)}
                                defaultSelectedRecipient={initialFaxNumber ? "--" : ""}
                                defaultSelectedFaxNumber={initialFaxNumber}
                            />
                        </div>
                    )}
                    fallbackFeature={(
                        <FaxAndSignatureClinicianSelection
                            useLabsSearch
                            useNameAndFaxOnly
                            StateUpdateDelegate={selectionState => setDestinatorInfoState(selectionState)}
                        />
                    )}
                />

                {cacheStore.ClinicSettings.EnableFaxCoverSheet && (
                    <FaxCoverSheetEditorSection
                        containerClassName="margin-md-bottom margin-lg-left"
                        subject={pdfCoverPageParams.CoverPageTitle}
                        message={pdfCoverPageParams.CoverPageContent}
                        isSubjectValid={showCoverSheetValidationErrors ? isCoverSheetSubjectValid : true}
                        isMessageValid={showCoverSheetValidationErrors ? isCoverSheetMessageValid : true}
                        validate={(isSubjectValid, isMessageValid) => {
                            setIsCoverSheetSubjectValid(isSubjectValid);
                            setIsCoverSheetMessageValid(isMessageValid);
                        }}
                        onCheckboxChange={value => {
                            setShowCoverSheetValidationErrors(false);
                            setPdfCoverPageParams(prev => ({ ...prev, GenerateCoverPage: value }));
                        }}
                        onSubjectChange={value => {
                            setShowCoverSheetValidationErrors(false);
                            setPdfCoverPageParams(prev => ({ ...prev, CoverPageTitle: value }));
                        }}
                        onMessageChange={value => {
                            setShowCoverSheetValidationErrors(false);
                            setPdfCoverPageParams(prev => ({ ...prev, CoverPageContent: value }));
                        }}
                    />
                )}

                {!returnFaxSender && (
                    <div className="attach-document-section">
                        <label className="form-label">{getText("DocumentGridFilesLabel")}</label>
                        <div className="attach-document-section-button">
                            <img
                                className="attach-document-icon"
                                src={img_icon_myle_pictogrammes_documents_31}
                                alt={getText("AttachADocumentLabel")}
                            />
                            <FileUploadLite
                                showFilename
                                acceptedFormat={["application/pdf"]}
                                className="FaxFileUpload"
                                uploadButtonText={getText("AttachADocumentLabel")}
                                onCompleteCallback={path => setFilePath(path)}
                                fileDestinationPath={2}
                            />
                        </div>
                    </div>
                )}

                {showSelectedDocumentPages && (
                    <div className="margin-lg-left margin-xs-top flex-row">
                        <label className="form-label margin-sm-right">{getText("DocumentGridFilesLabel")}</label>
                        <div className="flex-row flex-col-gap-xs">
                            <p>{selectedDocumentPages.length}</p>
                            <p>{getText("PagesLabel").toLowerCase()}</p>
                            <p>{`(${selectedDocumentPages.map(document => document.pageNumber).join(", ")})`}</p>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export { OutboundFaxCreationPopup };
